import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { numberOfProducts, numberOfProductsNoPlusSign } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import type { ErrorMessageFunction } from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentfulPiece';
import type { SubscriptionTiersProductPrice } from 'bundles/coursera-plus-landing-page/contentfulData/LandingPageDataProvider';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';

export type PropsFromCaller = {
  translatedMessage: string;
  blockDiscount?: boolean;
  showDiscountAmount?: boolean;
  priceAndSaving?: boolean;
  isPrimary: boolean;
  isStrikeThroughValue?: boolean;
  monthlyFromAnnualRoundedToNearestValue?: number;
} & ErrorMessageFunction;

export const priceElement = (price: SubscriptionTiersProductPrice): JSX.Element => {
  const { amount, currencyCode } = price;
  return <ReactPriceDisplay value={amount} currency={currencyCode} hideCurrencyCode={true} />;
};

export const PricedPretranslated = (props: PropsFromCaller) => {
  const landingPageExtended = useLandingPageExtended();
  const { prices } = landingPageExtended;
  const {
    translatedMessage,
    blockDiscount,
    showDiscountAmount,
    priceAndSaving,
    isPrimary,
    isStrikeThroughValue,
    monthlyFromAnnualRoundedToNearestValue,
  } = props;

  const { primaryPrice, primaryOriginalPrice, secondaryPrice } = prices;

  const getPrice = (original?: boolean): SubscriptionTiersProductPrice => {
    if (isPrimary) {
      if (!primaryPrice) {
        throw new Error('Missing Primary Price from price display');
      }
      if (blockDiscount || showDiscountAmount || original) {
        if (!primaryOriginalPrice) {
          throw new Error('Missing original price from promotion discount price display');
        }
      }
      if (blockDiscount || original) {
        return primaryOriginalPrice as SubscriptionTiersProductPrice;
      }
      if (showDiscountAmount) {
        const calculatedDiscountAmount =
          (primaryOriginalPrice as SubscriptionTiersProductPrice).amount -
          (primaryPrice as SubscriptionTiersProductPrice).amount;
        return { ...primaryPrice, amount: calculatedDiscountAmount } as SubscriptionTiersProductPrice;
      }

      if (monthlyFromAnnualRoundedToNearestValue) {
        const MONTHS_IN_A_YEAR = 12;
        const monthlyPriceAmount = Math.round(primaryPrice.amount / MONTHS_IN_A_YEAR);
        const customAmountNumber =
          Math.ceil(monthlyPriceAmount / monthlyFromAnnualRoundedToNearestValue) *
          monthlyFromAnnualRoundedToNearestValue;
        return {
          ...primaryPrice,
          amount: customAmountNumber,
        } as SubscriptionTiersProductPrice;
      }
      return primaryPrice;
    } else {
      if (!secondaryPrice) {
        throw new Error('Missing Secondary Price from price display');
      }
      return secondaryPrice;
    }
  };

  if (priceAndSaving) {
    const original = getPrice(true);
    const discounted = getPrice();
    const savingAmount = original.amount - discounted.amount;
    const saving = { ...discounted, amount: savingAmount } as SubscriptionTiersProductPrice;

    return (
      <FormattedMessage
        message={translatedMessage}
        price={priceElement(discounted)}
        saving={priceElement(saving)}
        numberOfProducts={numberOfProducts}
        numberOfProductsNoPlusSign={numberOfProductsNoPlusSign}
      />
    );
  }

  return isStrikeThroughValue ? (
    <s>
      <FormattedMessage
        message={translatedMessage}
        price={priceElement(getPrice())}
        numberOfProducts={numberOfProducts}
        numberOfProductsNoPlusSign={numberOfProductsNoPlusSign}
      />
    </s>
  ) : (
    <FormattedMessage
      message={translatedMessage}
      price={priceElement(getPrice())}
      numberOfProducts={numberOfProducts}
      numberOfProductsNoPlusSign={numberOfProductsNoPlusSign}
    />
  );
};

export default PricedPretranslated;
